import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Line } from 'recharts';
import { Link, useNavigate } from 'react-router-dom';
import { ROTAS } from '@/routes/routePaths';
import { FaMobile, FaMobileAlt, FaPhone } from 'react-icons/fa';

const mockImages = {
    hero: "/Aeroporto1.jpeg",
    appPreview: "/Aeroporto3.jpeg",
    coins: "/JardimNorte1.JPG",
    restaurant: "JardimNorte2.JPG",
    beer: "/Aeroporto2.jpeg",
    food: "/Aeroporto4.jpeg"
};

const FeatureCard = ({ title, description, icon, delay }: { title: string; description: string; icon: string; delay: number }) => (<>
    <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay }}
        viewport={{ once: true }}
        className="bg-background p-6 rounded-2xl shadow-lg border border-primary hover:border-primary/30 transition-all duration-300 group"
    >
        <div className="text-4xl mb-4 transition-transform duration-300">{icon}</div>
        <h3 className="text-xl font-bold mb-2 text-primary">{title}</h3>
        <p className="text-text/70">{description}</p>
    </motion.div>
</>
);

// Componente de Estatística
const StatCard = ({ value, label, delay }: { value: string; label: string; delay: number }) => (
    <motion.div
        initial={{ opacity: 0, scale: 0.8 }}
        whileInView={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5, delay }}
        viewport={{ once: true }}
        className="text-center p-6 bg-primary/10 rounded-2xl"
    >
        <div className="text-4xl font-bold text-primary mb-2">{value}</div>
        <div className="text-text/70">{label}</div>
    </motion.div>
);

const LandingPage: React.FC = () => {
    const [activeSection, setActiveSection] = useState('inicio');
    const [isMenuOpen, setIsMenuOpen] = useState(false);

    useEffect(() => {
        const handleScroll = () => {
            const sections = document.querySelectorAll('section');
            let currentSection = 'inicio';

            sections.forEach(section => {
                const sectionTop = section.offsetTop;
                if (window.scrollY >= sectionTop - 100) {
                    currentSection = section.id;
                }
            });

            setActiveSection(currentSection);
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const scrollToSection = (sectionId: string) => {
        const section = document.getElementById(sectionId);
        if (section) {
            // Calcula a posição considerando o header fixo
            const sectionTop = section.getBoundingClientRect().top + window.pageYOffset - 80;

            window.scrollTo({
                top: sectionTop,
                behavior: 'smooth'
            });
            setActiveSection(sectionId);
            setIsMenuOpen(false);
        }
    };

    const navigate = useNavigate();
    return (
        <div className="min-h-screen bg-background text-text">
            <header className="fixed w-full bg-gradient-to-r from-[#c5a663] via-[#f2dd82] to-[#c5a663] backdrop-blur-sm z-50 py-4 border-b border-yellow-700/30">
                <div className="container mx-auto flex justify-between items-center">


                    <nav className="hidden md:flex space-x-8">
                        {[
                            { id: 'inicio', label: 'Início' },
                            { id: 'como-funciona', label: 'Como Funciona' },
                            { id: 'como-funciona', label: 'Vantagens' },
                            { id: 'TugasCoins', label: 'Tugas Coins' },
                            { id: 'unidades', label: 'Unidades' }
                        ].map((item) => (
                            <motion.a
                                key={item.id}
                                onClick={() => scrollToSection(item.id)}
                                whileHover={{ scale: 1.05 }}
                                className={`cursor-pointer transition-colors ml-20 text-black`}
                            >
                                {item.label}
                            </motion.a>
                        ))}
                    </nav>

                    <div className="flex items-center space-x-4 ml-6">
                        <motion.button
                            whileHover={{ scale: 1.05 }}
                            onClick={() => navigate(ROTAS.LOGIN)}
                            className="bg-black text-white px-6 py-2 rounded-full font-medium shadow-lg hover:bg-gradient-to-r hover:from-[#c5a663] hover:via-[#cebc6e] hover:to-[#c5a663] hover:bg-yellow-100 transition-all"
                        >
                            Entrar
                        </motion.button>

                        <motion.button
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            onClick={() => navigate(ROTAS.CADASTRO)}
                            className="bg-black text-white px-6 py-2 rounded-full font-medium shadow-lg hover:bg-gradient-to-r hover:from-[#c5a663] hover:via-[#cebc6e] hover:to-[#c5a663] hover:bg-yellow-100 transition-all"
                        >
                            Cadastrar
                        </motion.button>
                    </div>
                </div>

                <AnimatePresence>
                    {isMenuOpen && (
                        <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: 'auto' }}
                            exit={{ opacity: 0, height: 0 }}
                            className="md:hidden bg-gradient-to-r from-[#c5a663] via-[#f2dd82] to-[#c5a663] border-t border-yellow-700/30 mt-4"
                        >
                            <div className="container mx-auto py-4 flex flex-col space-y-4">
                                {[
                                    { id: 'inicio', label: 'Início' },
                                    { id: 'como-funciona', label: 'Como Funciona' },
                                    { id: 'como-funciona', label: 'Vantagens' },
                                    { id: 'TugasCoins', label: 'TugasCoins' },
                                    { id: 'unidades', label: 'Unidades' }
                                ].map((item) => (
                                    <a
                                        key={item.id}
                                        onClick={() => scrollToSection(item.id)}
                                        className={`cursor-pointer py-2 text-white ${activeSection === item.id ? 'font-bold underline' : ''}`}
                                    >
                                        {item.label}
                                    </a>
                                ))}
                                <div className="pt-4 border-t border-yellow-700/30">
                                    <button className="w-full text-center py-2 text-white hover:text-yellow-200">
                                        Entrar
                                    </button>
                                </div>
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </header>
            <section id="inicio" className="pt-20 pb-10 px-6 gap-40 relative min-h-screen flex items-center z-40">
                {/* Imagem de fundo com borda arredondada na parte inferior */}
                <div className="absolute inset-0 z-0">
                    <img
                        src="/Background.png"
                        alt="Background"
                        className="w-full h-full object-cover rounded-b-[50px]"
                    />
                    <div className="absolute inset-0 bg-black/10"></div>
                </div>

                <div className="container mx-auto grid md:grid-cols-2 gap-10 sm:gap-20 md:gap-30 items-center relative z-10">
                    {/* Lado esquerdo - Quadro preto com conteúdo */}
                    <motion.div
                        initial={{ opacity: 0, x: -50 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.7 }}
                        className="bg-black/20 p-2 md:p-4 mx-4 text-white rounded-2xl border border-gray-800 shadow-2xl backdrop-blur-sm relative overflow-hidden"
                    >
                        {/* Background Image */}
                        <div className="absolute inset-0 z-0">
                            <img
                                src="/BG.png"
                                alt="Background"
                                className="h-full w-full object-cover rounded-2xl sm:opacity-90 xs:opacity-80 opacity-70"
                            />
                            {/* Overlay para melhor contraste do texto */}
                            <div className="absolute inset-0 bg-black/5 rounded-2xl"></div>
                        </div>

                        {/* Conteúdo */}
                        <div className="relative z-10">
                            {/* Header com Brasão */}
                            <div className="flex items-center justify-center ml-14 mt-4">
                                <motion.img
                                    src="/clube.png"
                                    alt="Brasão Clube Mr. Tugas"
                                    className="h-90 md:h-80 object-contain -mt-14" // ou -mt-12, -mt-16, etc
                                    whileHover={{ scale: 1.05 }}
                                    whileTap={{ scale: 0.95 }}
                                />

                            </div>

                            {/* Título Principal */}
                            <h2 className="text-xl md:text-2xl -mt-4 sm:-mt-20 justify-self-center lg:text-3xl px-6 xs:px-24 font-bold mb-6 leading-tight text-white">
                                Entre para o Clube
                                e Ganhe <span className="text-primary">Cashback</span>.
                            </h2>

                            {/* Descrição */}
                            <p className="text-sm md:text-base justify-self-center mb-2 px-6 xs:px-24 text-white leading-relaxed">
                                Agora, o Mr. Tugas tem um Clube de
                                Vantagens para você aproveitar o que temos
                                de melhor, pagando bem menos. Na Promoção de
                                Aniversário 21 anos, você paga R$ 2.500,00 e
                                leva R$ 5.000,00 em Tugas Coins (100% de
                                cashback) para consumir no local ou retirar
                                nas nossas unidades Aeroporto e Jardim Norte,
                                incluindo a store e eventos na Cave. Limitado
                                a 1 compra por conta. Não vale para delivery
                                de chope e delivery de pizza.
                            </p>
                            <p className="text-sm md:text-base justify-self-center mb-2 px-6 xs:px-24  text-white leading-relaxed mb-10">
                                <b>Conheça todos os detalhes e aproveite essa  oportunidade única, afinal, todo bom Clube  tem vagas limitadas.</b>
                            </p>
                        </div>
                    </motion.div>

                    {/* Lado direito - Dois quadradinhos */}
                    <motion.div
                        initial={{ opacity: 0, x: 50 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.7, delay: 0.2 }}
                        className="flex gap-4 md:mt-60 md:justify-end justify-center items-center mb-10 " // Flex com gap
                    >
                        {/* Primeiro quadradinho (/50cashback.png, "50% Cashback Mínimo") oculto:
                            na Promoção de Aniversário 21 anos o cashback é só de 100%. */}
                        <motion.div
                            initial={{ opacity: 0, x: 50 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.7, delay: 0.2 }}
                            className="flex gap-4 " // Flex com gap
                        >
                            {/* Segundo quadradinho - 100% Cashback */}
                            <motion.img
                                src="/100cashback.png"
                                alt="100% Cashback"
                                className="h-20 md:h-40 object-contain flex-1 self-start md:mt-20 "
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                            />
                        </motion.div>
                    </motion.div>
                </div>
                <div className="flex justify-center bottom-[-20px] mt-20 w-full absolute z-10" >
                    <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={() => navigate(ROTAS.CADASTRO)}
                        className="bg-gradient-to-r from-[#c5a663] mr-12 via-[#f2dd82] to-[#c5a663] text-background px-8 py-6 rounded-full font-extrabold shadow-lg hover:shadow-primary/30 hover:bg-gradient-to-r from-[#c5a663] via-[#f2dd82] to-[#c5a663] transition-all"
                    >
                        Entre para o Clube agora
                    </motion.button>
                </div>
            </section>


            {/* How It Works Section */}
            <section id="como-funciona" className="py-16 px-6 bg-background/50 -mt-28 relative overflow-hidden">
                {/* Background Image */}
                <div className="absolute inset-0 z-10 rounded-b-[50px] overflow-hidden">
                    <img
                        src="/Background-secao2.png"
                        alt="Background"
                        className="w-full h-full object-cover rounded-b-[50px]"
                    />
                    {/* Overlay para melhor contraste */}
                    <div className="absolute inset-0 bg-black/5 rounded-b-[50px]"></div>
                </div>

                <div className="container mx-auto relative z-10 py-10">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5 }}
                        viewport={{ once: true }}
                        className="text-center mb-16"
                    >
                        <h1 className="text-3xl md:text-4xl font-bold mb-4 text-primary text-start mt-20">Como Funciona?</h1>
                        <p className="text-lg mx-auto text-white/80 text-start">
                            Fazer parte do Clube Mr. Tugas é simples, você só precisa:
                        </p>
                    </motion.div>

                    <div className="grid md:grid-cols-3 gap-8 text-white">
                        <FeatureCard
                            title="Fazer seu cadastro"
                            description="Cadastro simples, rápido e seguro."
                            icon={<img src="/user.png" alt="TugasCoins" className="h-14 max-h-14" />}
                            delay={0.1}
                        />
                        <FeatureCard
                            title="Realizar uma compra antecipada"
                            description="Compre R$ 2.500,00 em Tugas Coins pela plataforma, via PIX (1 compra por conta)"
                            icon={<img src="/carrinho.png" alt="TugasCoins" className="h-14 max-h-14" />}
                            delay={0.2}
                        />
                        <FeatureCard
                            title="Ganhe 100% de Cashback"
                            description="Receba mais R$ 2.500,00 de cashback: são R$ 5.000,00 para usar após 48 horas"
                            icon={<img src="/estrela.png" alt="TugasCoins" className="h-14 max-h-14" />}
                            delay={0.3}
                        />
                    </div>



                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5 }}
                        viewport={{ once: true }}
                        className="text-center mb-16"
                    >
                        <h1 className="text-3xl md:text-4xl font-bold mb-4 text-primary text-start mt-20">Vantagens Exclusivas</h1>
                        <p className="text-lg mx-auto text-white/80 text-start">
                            Descubra todos os benefícios de fazer parte do programa de fidelidade Mr. Tugas
                        </p>
                    </motion.div>

                    <div className="flex items-center">
                        <div className="space-y-0 grid grid-cols-1 xs:grid-cols-1 md:grid-cols-2 gap-4">
                            {[
                                {
                                    title: "Promoção de Aniversário 21 anos!",
                                    description: <>Pague R$2.500,00, leve R$5.000,00 (100% de cashback).<br /> Valor único, limitado a 1 compra por conta.</>,
                                    icon: <img
                                        src="/Icone 1.png"
                                        alt="Tugas Coins"
                                        className="w-16 h-16 object-contain"
                                    />
                                },
                                {
                                    title: "Sem data de validade",
                                    description: "Você faz sua compra antecipada, ganha o cashback e pode utilizá-lo de uma vez só ou aos poucos, a qualquer momento até finalizar seu saldo.",
                                    icon: <img
                                        src="/Icone 2.png"
                                        alt="Tugas Coins"
                                        className="w-16 h-16 object-contain"
                                    />
                                },
                                {
                                    title: "Pagamento rápido e seguro",
                                    description: "Compra realizada exclusivamente pela plataforma, com pagamento via pix.",
                                    icon: <img
                                        src="/Icone 3.png"
                                        alt="Tugas Coins"
                                        className="w-16 h-16 object-contain"
                                    />
                                },
                                {
                                    title: "Consumo no local ou retirada",
                                    description: <>Use suas Tugas Coins nas Unidades Aeroporto e Jardim Norte, para consumo no local ou retirada, incluindo produtos da Store e eventos na Cave. <span className='text-black'><br />*Não vale para delivery de chope e delivery de pizza</span> </>,
                                    icon: <img
                                        src="/Icone 4.png"
                                        alt="Tugas Coins"
                                        className="w-16 h-16 object-contain"
                                    />
                                },
                            ].map((feature, index) => (
                                <motion.div
                                    key={index}
                                    initial={{ opacity: 0, x: -30 }}
                                    whileInView={{ opacity: 1, x: 0 }}
                                    transition={{ duration: 0.5, delay: index * 0.1 }}
                                    viewport={{ once: true }}
                                    className="items-start p-2 bg-gradient-to-r from-[#c5a663] via-[#bdad65] to-[#c5a663] rounded-2xl hover:bg-primary/5 flex flex-col-2 transition-colors backdrop-blur-sm"
                                >
                                    <div className="flex-shrink-0 mr-4 bg-primary/20 rounded-lg p-2">
                                        {feature.icon}
                                    </div>
                                    <div className="flex-1">
                                        <h3 className="font-bold text-lg mb-1 text-black">{feature.title}</h3>
                                        <p className="text-white/80">{feature.description}</p>
                                    </div>
                                </motion.div>
                            ))}
                        </div>
                    </div>

                    <div className="flex justify-center mt-12">
                        <motion.button
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            onClick={() => navigate(ROTAS.CADASTRO)}
                            className="bg-gradient-to-r from-[#c5a663] mr-6 via-[#f2dd82] to-[#c5a663] text-background px-8 py-6 rounded-full font-extrabold shadow-lg hover:shadow-primary/30 hover:bg-gradient-to-r from-[#c5a663] via-[#f2dd82] to-[#c5a663] transition-all"
                        >
                            Entre para o Clube agora
                        </motion.button>
                    </div>
                </div>
            </section>

            {/* App Preview Section */}
            {/* Container com background contínuo para TugasCoins e Unidades */}
            <div className="relative -mt-10">
                {/* Background que cobre TugasCoins e Unidades */}
                <div className="absolute inset-0 z-0 overflow-hidden">
                    <img
                        src="/mrtugasaeroporto.png"
                        alt="Background Mr. Tugas Aeroporto"
                        className="w-full h-full object-cover"
                    />
                    {/* Overlay para melhor contraste */}
                    <div className="absolute inset-0 bg-black/10"></div>
                </div>

                {/* App Preview Section - MANTIDO ORIGINAL */}
                <section id="TugasCoins" className="py-20 px-6 relative overflow-hidden">
                    <div className="container mx-auto relative z-10">
                        <div className="container mx-auto grid md:grid-cols-2 gap-10 sm:gap-20 md:gap-30 items-center relative z-10">
                            <motion.div
                                initial={{ opacity: 0, x: 50 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ duration: 0.7, delay: 0.2 }}
                                className="flex gap-4 md:mt-60 md:justify-end justify-center items-center mb-10"
                            >
                            </motion.div>

                            {/* Lado esquerdo - Quadro preto com conteúdo - MANTIDO ORIGINAL */}
                            <motion.div
                                initial={{ opacity: 0, x: -50 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ duration: 0.7 }}
                                className="bg-gradient-to-r from-[#c5a663] via-[#fde888] to-[#c5a663] mt-10 p-2 md:p-4 mx-4 text-white rounded-2xl shadow-2xl backdrop-blur-sm relative overflow-hidden"
                            >
                                <motion.div
                                    initial={{ opacity: 0, x: 30 }}
                                    whileInView={{ opacity: 1, x: 0 }}
                                    transition={{ duration: 0.7 }}
                                    viewport={{ once: true }}
                                >
                                    <img
                                        src="/coins.png"
                                        alt="Tugas Coins"
                                        className="w-70 sm:w-90 mt-10 object-cover justify-self-center"
                                    />
                                    <h3 className='text-black px-4 mx-0 sm:mx-10'>Essa é a moeda oficial <br /> do nosso clube.</h3>
                                    <p className='px-1 py-4 rounded-full text-center bg-black text-primary sm:mx-10 '>
                                        <b><i>1 real é igual a 1 Tugas Coin.</i></b>
                                    </p>
                                    <p className="text-md mb-2 px-4 text-black sm:mx-10">
                                        E com ela, você pode consumir o que quiser nas unidades Aeroporto e Jardim Norte,
                                        no local ou para retirada, incluindo produtos da Store e eventos na Cave.<br />
                                        <span className='text-black'>*Não vale para delivery de chope e delivery de pizza</span><br />  <br />
                                    </p>
                                    <p className="text-sm mb-2 px-4 flex text-black"><FaMobileAlt className='size-12 mr-6' />

                                        E mais: Tenha controle total sobre suas Tugas Coins diretamente no seu smartphone.
                                    </p>

                                    <div className="space-y-2 mb-8 text-sm ml-10">
                                        {[
                                            "Acompanhe seu saldo em tempo real",
                                            "Visualize seu histórico completo de transações",
                                            "Receba notificações de promoções exclusivas",
                                        ].map((feature, index) => (
                                            <div key={index} className="flex items-center">
                                                <div className="w-4 h-4 text-sm bg-black/70 rounded-full flex items-center justify-center mr-3">
                                                    <span className="text-primary">✓</span>
                                                </div>
                                                <span className="text-black text-sm">{feature}</span>
                                            </div>
                                        ))}
                                    </div>
                                </motion.div>
                            </motion.div>
                        </div>
                    </div>
                </section>

                {/* Units Section - MANTIDO ORIGINAL (só removi o background próprio) */}
                <section id="unidades" className="py-20 px-6 relative ">
                    <div className="container mx-auto mt-40">
                        <motion.div
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5 }}
                            viewport={{ once: true }}
                            className="text-center mb-16"
                        >
                            <h2 className="text-3xl md:text-4xl font-bold mb-4 sm:text-primary xs:text-primary text-white text-start">Nossas Unidades</h2>
                            <p className="text-lg mx-auto text-white text-start">
                                Use suas Tugas Coins em qualquer uma de nossas unidades
                            </p>
                        </motion.div>

                        <div className="grid md:grid-cols-3 gap-8">
                            {[
                                {
                                    name: "Mr. Tugas Jardim Norte",
                                    image: mockImages.restaurant,
                                    address: "Av. Brasil, 6345 - Loja 1039",
                                    hours1: "Dom a qui de 17:00 às 23:00",
                                    hours2: "Sex e Sab de 17:00 às 00:00"
                                },
                                {
                                    name: "Mr. Tugas Aeroporto",
                                    image: mockImages.beer,
                                    address: "R. Otília de Souza Leal, 310",
                                    hours1: "Seg a sex de 18:00 às 23:00",
                                    hours2: "Sab e Dom 18:00 às 00:00"
                                },
                                {
                                    name: "Cervejaria",
                                    image: mockImages.food,
                                    address: "R. Otília de Souza Leal, 310",
                                    hours1: "Seg a sex de 18:00 às 23:00",
                                    hours2: " Sab e Dom 18:00 às 00:00"
                                }
                            ].map((unit, index) => (
                                <motion.div
                                    key={index}
                                    initial={{ opacity: 0, y: 20 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.5, delay: index * 0.1 }}
                                    viewport={{ once: true }}
                                    className="bg-background rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-shadow"
                                >
                                    <div className="h-48 overflow-hidden">
                                        <img
                                            src={unit.image}
                                            alt={unit.name}
                                            className="w-full h-full object-cover"
                                        />
                                    </div>
                                    <div className="p-6">
                                        <h3 className="text-xl text-center font-bold mb-2 text-primary">{unit.name}</h3>
                                        <p className="text-text/70 text-center mb-2">{unit.address}</p>
                                        <p className="text-text/70 text-center">{unit.hours1}</p>
                                        <p className="text-text/70 text-center">{unit.hours2}</p>
                                    </div>
                                </motion.div>
                            ))}
                        </div>
                    </div>
                </section>
            </div>

            {/* Footer */}
            <footer className="py-12 px-6 bg-background/80 border-t border-primary/10">
                <div className="container mx-auto">
                    <div className="grid md:grid-cols-4 gap-8">
                        <div>
                            <div className="flex items-center space-x-3 mb-4">

                                <h3 className="font-bold mb-4 whitespace-nowrap text-primary">Clube Mr. Tugas</h3>
                            </div>
                            <p className="text-text/70">
                                Entre para o Clube. Ganhe Cashback.
                            </p>
                            <ul className="space-y-2">
                                <li key={1}>
                                    <a
                                        onClick={() => navigate('/termos-condicoes')}
                                        className="text-text/70 hover:text-primary transition-colors cursor-pointer"
                                    >
                                        Termos e Condições 
                                    </a>
                                </li>
                                <li key={2}>
                                    <a
                                        onClick={() => navigate('/politica-privacidade')}
                                        className="text-text/70 hover:text-primary transition-colors cursor-pointer"
                                    >
                                        Política de Privacidade
                                    </a>
                                </li></ul>
                            <motion.button
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                                onClick={() => navigate(ROTAS.CADASTRO)}
                                className="bg-gradient-to-r from-[#c5a663] mr-6 via-[#f2dd82] to-[#c5a663] text-background px-8 py-4 rounded-full font-extrabold shadow-lg hover:shadow-primary/30 hover:bg-gradient-to-r from-[#c5a663] via-[#f2dd82] to-[#c5a663] transition-all"
                            >
                                Entrar agora
                            </motion.button>
                        </div>

                        <div>
                            <h3 className="font-bold mb-4 text-primary">Links Rápidos</h3>
                            <ul className="space-y-2">
                                {['Início', 'Como Funciona', 'TugasCoins', 'Unidades'].map((item) => (
                                    <li key={item}>
                                        <a
                                            onClick={() => scrollToSection(item.toLowerCase().replace(' ', '-'))}
                                            className="text-text/70 hover:text-primary transition-colors cursor-pointer"
                                        >
                                            {item}
                                        </a>
                                    </li>
                                ))}
                            </ul>
                        </div>

                        <div>
                            <h3 className="font-bold mb-4 text-primary">Contatos</h3>
                            <ul className="space-y-2">
                                <li className="text-text/70">adm2@mrtugas.com.br</li>
                                {[{ "nome": 'Jardim Norte', "telefone": "+55 (32) 98451-9383 e +55 (32) 3321-9582" }, { "nome": 'Aeroporto', "telefone": `+55 (32) 3233-0036 ` }, { "nome": 'Cervejaria', "telefone": "+55 (32) 98846-1959" }].map((unit) => (
                                    <li key={unit.nome} className="text-text/70">
                                        {unit.nome}<><br /></>
                                        {unit.telefone}
                                    </li>
                                ))}
                            </ul>
                        </div>

                        <div>
                            <h3 className="font-bold mb-4 text-primary">Redes Sociais</h3>
                            <ul className="space-y-2">
                                <li className="flex space-x-4 pt-2">
                                    {/* Facebook */}
                                    <a href="https://www.facebook.com/MrTugas" target='_blank' className="text-black hover:text-[#1877F2] transition-colors">
                                        <span className="sr-only">Facebook</span>
                                        <div className="w-8 h-8 bg-primary/40 rounded-full flex items-center justify-center hover:bg-[#1877F2]/10 transition-colors">
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                                                <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                                            </svg>
                                        </div>
                                    </a>

                                    {/* Instagram */}
                                    <a href="https://www.instagram.com/mr_tugas" target='_blank' className="text-black hover:text-[#E4405F] transition-colors">
                                        <span className="sr-only">Instagram</span>
                                        <div className="w-8 h-8 bg-primary/40 rounded-full flex items-center justify-center hover:bg-[#E4405F]/10 transition-colors">
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                                                <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
                                            </svg>
                                        </div>
                                    </a>

                                    {/* Website */}
                                    <a href="https://www.mrtugas.com.br" target='_blank' className="text-black hover:text-primary transition-colors">
                                        <span className="sr-only">Website</span>
                                        <div className="w-8 h-8 bg-primary/40 rounded-full flex items-center justify-center hover:bg-primary/20 transition-colors">
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                                                <circle cx="12" cy="12" r="10" />
                                                <line x1="2" y1="12" x2="22" y2="12" />
                                                <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
                                            </svg>
                                        </div>
                                    </a>
                                </li>
                            </ul>
                        </div>

                    </div>

                    <div className="border-t border-text/20 mt-12 pt-8 text-center text-text/60">
                        <p>© {new Date().getFullYear()} Clube Mr. Tugas. Todos os direitos reservados.</p>
                    </div>
                </div>
            </footer>
        </div >
    );
};

export default LandingPage;